import React, { useState } from 'react';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FiCalendar, FiSearch } from 'react-icons/fi';
import { lookupOrders, QBOrderDetail } from '../api/bubbletea';
import { localizeOptionsEntries } from '../utils/bubbleTeaOptionPricing';
import { getStatusLabel, formatQueueNumberValue } from '../utils/orderStatus';
import { localizeErrorDetail, getErrorDetail } from '../utils/errorDetail';
import axiosInstance from '../api/axios';
import type { SupportedLanguage } from '../i18n';
import type { BubbleTeaOptionCategory } from '../api/adminBubbleTeaOptions';

const Container = styled.div`
  min-height: calc(100vh - var(--header-height));
  padding: 2rem 1rem;
  max-width: 800px;
  margin: 0 auto;
  margin-top: var(--header-height);

  @media (min-width: 768px) {
    padding: 3rem 2rem;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  margin-bottom: 0.5rem;
  color: var(--text-color);
`;

const Subtitle = styled.p`
  color: var(--gray-dark);
  margin-bottom: 2rem;
  font-size: 0.95rem;
`;

const FormCard = styled.div`
  background: var(--white);
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text-color);
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid var(--gray-medium);
  border-radius: 6px;
  font-size: 1rem;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(139, 69, 19, 0.1);
  }
`;

const ValidationError = styled.p`
  color: #c62828;
  font-size: 0.85rem;
  margin-top: 0.25rem;
`;

const Button = styled.button`
  padding: 0.875rem;
  background-color: var(--primary-color);
  color: var(--white);
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:hover:not(:disabled) {
    background-color: var(--primary-dark);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  background-color: #ffebee;
  color: #c62828;
  padding: 0.75rem;
  border-radius: 6px;
  font-size: 0.9rem;
  text-align: center;
`;

const OrdersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const OrderCard = styled.div`
  background: var(--white);
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const OrderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const OrderNumber = styled.h3`
  font-size: 1.125rem;
  color: var(--text-color);
`;

const OrderStatus = styled.span<{ $status: string }>`
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 500;
  background-color: ${props => {
    switch (props.$status) {
      case 'pending':
        return 'var(--warning-light)';
      case 'confirmed':
        return 'var(--info-light)';
      case 'ready':
        return 'var(--success-light)';
      case 'completed':
        return 'var(--success-color)';
      default:
        return 'var(--gray-light)';
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'pending':
        return 'var(--warning-dark)';
      case 'confirmed':
        return 'var(--info-dark)';
      case 'ready':
        return 'var(--success-dark)';
      case 'completed':
        return 'var(--white)';
      default:
        return 'var(--text-color)';
    }
  }};
`;

const OrderInfo = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  color: var(--gray-dark);
  font-size: 0.875rem;
  margin-bottom: 0.75rem;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const ItemsList = styled.ul`
  margin: 0;
  padding: 0 0 0 1rem;
  font-size: 0.875rem;
  color: var(--text-color);
`;

const ItemEntry = styled.li`
  margin-bottom: 0.25rem;
`;

const ItemOptions = styled.span`
  color: var(--gray-dark);
  font-size: 0.8rem;
`;

const EmptyState = styled.div`
  background: var(--white);
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  color: var(--gray-dark);
`;

const LoadingState = styled.div`
  background: var(--white);
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  color: var(--gray-dark);
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  margin-bottom: 1rem;
  color: var(--text-color);
`;

const QBOrderLookupPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  // Mirrors the LanguageToggle.tsx pattern: i18next's language can be a
  // region-qualified tag or unset, so narrow to the two v1-supported values.
  const language: SupportedLanguage = (i18n.language as SupportedLanguage) === 'zh' ? 'zh' : 'en';
  const [phone, setPhone] = useState('');
  const [queueNumber, setQueueNumber] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [queueError, setQueueError] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState<QBOrderDetail[] | null>(null);

  // Guest page, no auth gate -- the option-categories endpoint is public
  // (GET /quick-bites/options/categories, no auth dependency), so this
  // fetch is unconditional, unlike AccountOrders.tsx's isAuthenticated-gated
  // equivalent. Re-resolves each order item's localized option names against
  // the live catalog, with English fallback (see localizeOptionsEntries).
  const { data: rawOptionCategories } = useQuery<BubbleTeaOptionCategory[]>({
    queryKey: ['bubbleTeaOptionCategories'],
    queryFn: async () => {
      const res = await axiosInstance.get<BubbleTeaOptionCategory[]>('/quick-bites/options/categories');
      return res.data;
    },
  });
  const optionCategories: BubbleTeaOptionCategory[] = Array.isArray(rawOptionCategories)
    ? rawOptionCategories.filter((c: BubbleTeaOptionCategory) => c.active)
    : [];

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getItemDisplayName = (item: { menu_item_name: string; menu_item_name_zh: string | null }): string => {
    if (language === 'zh' && item.menu_item_name_zh && item.menu_item_name_zh.trim() !== '') {
      return item.menu_item_name_zh;
    }
    return item.menu_item_name;
  };

  const formatOptions = (options: Record<string, string> | null): string => {
    if (!options) return '';
    return localizeOptionsEntries(options, optionCategories, language)
      .map(({ key, value }) => `${key}: ${value}`)
      .join(', ');
  };

  const validate = (): boolean => {
    let valid = true;
    if (!phone.trim()) {
      setPhoneError(t('qbOrderLookup.phoneRequiredError'));
      valid = false;
    } else {
      setPhoneError('');
    }
    if (!queueNumber.trim()) {
      setQueueError(t('qbOrderLookup.queueRequiredError'));
      valid = false;
    } else {
      setQueueError('');
    }
    return valid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setError('');
    setResults(null);

    try {
      const orders = await lookupOrders(phone.trim(), parseInt(queueNumber, 10));
      setResults(orders);
    } catch (err: any) {
      setError(localizeErrorDetail(getErrorDetail(err), t) || t('qbOrderLookup.lookupFailedError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Title>{t('qbOrderLookup.title')}</Title>
      <Subtitle>{t('qbOrderLookup.subtitle')}</Subtitle>

      <FormCard>
        <Form onSubmit={handleSubmit}>
          {error && <ErrorMessage>{error}</ErrorMessage>}

          <FormGroup>
            <Label htmlFor="lookup-phone">{t('qbOrderLookup.phoneLabel')}</Label>
            <Input
              id="lookup-phone"
              type="text"
              data-testid="lookup-phone"
              value={phone}
              onChange={e => {
                setPhone(e.target.value);
                setPhoneError('');
              }}
              placeholder={t('qbOrderLookup.phonePlaceholder')}
            />
            {phoneError && <ValidationError>{phoneError}</ValidationError>}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="lookup-queue-number">{t('qbOrderLookup.queueLabel')}</Label>
            <Input
              id="lookup-queue-number"
              type="number"
              data-testid="lookup-queue-number"
              value={queueNumber}
              onChange={e => {
                setQueueNumber(e.target.value);
                setQueueError('');
              }}
              placeholder={t('qbOrderLookup.queuePlaceholder')}
              min={1}
            />
            {queueError && <ValidationError>{queueError}</ValidationError>}
          </FormGroup>

          <Button type="submit" data-testid="lookup-submit" disabled={loading}>
            <FiSearch />
            {loading ? t('qbOrderLookup.searchingButton') : t('qbOrderLookup.submitButton')}
          </Button>
        </Form>
      </FormCard>

      {loading && <LoadingState>{t('qbOrderLookup.loadingOrders')}</LoadingState>}

      {!loading && results !== null && (
        <>
          <SectionTitle>{t('qbOrderLookup.resultsTitle')}</SectionTitle>
          {results.length === 0 ? (
            <EmptyState data-testid="no-orders-found">{t('qbOrderLookup.noOrdersFound')}</EmptyState>
          ) : (
            <OrdersList>
              {results.map(order => (
                <OrderCard key={order.id}>
                  <OrderHeader>
                    <OrderNumber>{t('qbOrderLookup.queueNumberLabel', { number: formatQueueNumberValue(order.queue_number, t('qbOrderLookup.notAvailable')) })}</OrderNumber>
                    <OrderStatus $status={order.status.toLowerCase()}>
                      {getStatusLabel(order.status, t)}
                    </OrderStatus>
                  </OrderHeader>
                  <OrderInfo>
                    <InfoItem>
                      <FiCalendar />
                      <span>{formatDate(order.created_at)}</span>
                    </InfoItem>
                  </OrderInfo>
                  <ItemsList>
                    {order.items.map(item => {
                      const optionsStr = formatOptions(item.options);
                      return (
                        <ItemEntry key={item.id}>
                          {item.quantity}x {getItemDisplayName(item)}
                          {optionsStr && (
                            <ItemOptions> ({optionsStr})</ItemOptions>
                          )}
                        </ItemEntry>
                      );
                    })}
                  </ItemsList>
                </OrderCard>
              ))}
            </OrdersList>
          )}
        </>
      )}
    </Container>
  );
};

export default QBOrderLookupPage;
